"use client"

import { useEffect, useState } from "react"
import { IconCheck, IconLoader2, IconUserPlus } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"
import { CARE_TEAM_SLOT_FIELD, updateCareTeam, type CareTeamSlots } from "@/lib/care-team-api"
import { getDoctors } from "@/lib/doctors-api"
import { getUsers } from "@/lib/users-api"
import { cn } from "@/lib/utils"

/**
 * Phase 82 — one care-team slot: shows who holds it and lets a
 * `care_team.assign` holder fill, change or clear it in place.
 *
 * Shape is lifted from {@link import('./quick-assign-button').QuickAssignButton}
 * — Popover + Command + a module-level cache — but the AUTHORIZATION is
 * deliberately different (P-9): that component gates on `leads.assign`, which
 * is admin/team_leader only and governs the sales agent. Care teams are set by
 * the people who book.
 *
 * The CLINICAL slots take their options from `GET /doctors` rather than the
 * generic user list, because that endpoint carries each doctor's `specialty` —
 * and specialty is the ONLY thing distinguishing the prosthetist from the
 * doctor, since both hold `role:doctor` and no prosthetist role exists (D-2).
 * The front-desk slot instead lists `role:frontdesk` users, which is why the
 * source is chosen per slot rather than fixed.
 *
 * Reassignment is allowed, not just first-fill (Q-4): doctors leave, cases are
 * handed over, slots get filled wrong. The lead activity log is the safeguard.
 */

/**
 * Cached across mounts, PER SOURCE — the directories are small and change
 * rarely. Keyed because the clinical slots and the front-desk slot list
 * different people; one shared cache would show doctors in the desk picker.
 */
const cachedPeople: Record<string, SlotPerson[] | null> = {}

/**
 * What this control renders, common to both directories. Front-desk users have
 * no doctor_profile, so specialty is simply absent for them.
 */
interface SlotPerson {
  id: number
  full_name: string
  doctor_profile?: { specialty?: string | null } | null
}

type CareTeamSlotId = "doctor" | "prosthetist" | "frontdesk"

interface CareTeamSlotProps {
  leadId: string
  /** Which slot this control writes. */
  slot: CareTeamSlotId
  currentId: number | null
  currentName: string | null
  onChanged?: () => void
  /** Compact mode for table rows: name only, no label, smaller trigger. */
  dense?: boolean
  className?: string
}

export function CareTeamSlot({
  leadId,
  slot,
  currentId,
  currentName,
  onChanged,
  dense = false,
  className,
}: CareTeamSlotProps) {
  const t = useTranslations("careTeam")
  const { can } = usePermissions()
  const careTeamEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.care_team_enabled ?? false,
  )

  const [open, setOpen] = useState(false)
  // Clinical slots read the doctor directory; the desk slot reads frontdesk users.
  const source = slot === "frontdesk" ? "frontdesk" : "doctors"
  const [doctors, setDoctors] = useState<SlotPerson[]>(cachedPeople[source] ?? [])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const canAssign = careTeamEnabled && can("care_team.assign")

  useEffect(() => {
    if (!open || cachedPeople[source]) return
    setIsLoading(true)
    // Mapped, not cast: User.id is typed string while Doctor.id is number, and
    // the care-team columns take the numeric user id.
    const load: Promise<SlotPerson[]> =
      source === "frontdesk"
        ? getUsers({ role: "frontdesk", is_active: true }).then((res) =>
            res.data.map((u) => ({ id: Number(u.id), full_name: u.full_name })),
          )
        : getDoctors({ is_active: true }).then((res) =>
            res.data.map((d) => ({
              id: d.id,
              full_name: d.full_name,
              doctor_profile: d.doctor_profile,
            })),
          )

    load
      .then((people) => {
        cachedPeople[source] = people
        setDoctors(people)
      })
      .catch(() => toast.error(t("loadFailed")))
      .finally(() => setIsLoading(false))
  }, [open, source, t])

  const save = async (userId: number | null) => {
    setIsSaving(true)
    try {
      // Send ONLY this slot: an omitted key is left alone server-side, so
      // editing the doctor can never wipe the prosthetist.
      const slots: CareTeamSlots = { [CARE_TEAM_SLOT_FIELD[slot]]: userId }

      await updateCareTeam(leadId, slots)
      toast.success(userId ? t("assigned") : t("cleared"))
      setOpen(false)
      onChanged?.()
    } catch {
      toast.error(t("assignFailed"))
    } finally {
      setIsSaving(false)
    }
  }

  const initials = (name: string) =>
    name
      .split(" ")
      .map((p) => p.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2)

  // HIDE, never disable — without the permission the slot is read-only text,
  // and an empty one simply reads as unassigned rather than offering a control
  // the viewer cannot use.
  if (!canAssign) {
    return (
      <span className={cn("truncate", !currentName && "text-text-dim", className)}>
        {currentName ?? t("unassigned")}
      </span>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-auto justify-start gap-1.5 px-1.5 py-0.5 font-normal",
            dense ? "text-[11px]" : "text-sm",
            !currentName && "text-text-dim",
            className,
          )}
          onClick={(e) => e.stopPropagation()}
          aria-label={
            currentName
              ? t(slot === "doctor" ? "changeDoctorAria" : "changeProsthetistAria")
              : t(slot === "doctor" ? "assignDoctorAria" : "assignProsthetistAria")
          }
        >
          {currentName ?? (
            <>
              <IconUserPlus className={dense ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
              {t("assign")}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[260px] p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={t("searchPlaceholder")} />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <IconLoader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty>{t("noDoctors")}</CommandEmpty>
                <CommandGroup>
                  <CommandItem onSelect={() => save(null)} disabled={isSaving}>
                    <div className="flex w-full items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-muted-foreground/50">
                        <span className="text-[10px] text-muted-foreground">?</span>
                      </div>
                      <span className="text-muted-foreground">{t("unassigned")}</span>
                      {currentId === null && <IconCheck className="ml-auto h-4 w-4" />}
                    </div>
                  </CommandItem>
                  {doctors.map((doctor) => (
                    <CommandItem
                      key={doctor.id}
                      // Search the specialty too — "prosthodontics" is how the
                      // desk finds the right person, not the surname.
                      value={`${doctor.full_name} ${doctor.doctor_profile?.specialty ?? ""}`}
                      onSelect={() => save(doctor.id)}
                      disabled={isSaving}
                    >
                      <div className="flex w-full items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-[10px] font-medium">
                            {initials(doctor.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="min-w-0">
                          <span className="block truncate">{doctor.full_name}</span>
                          {doctor.doctor_profile?.specialty && (
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {doctor.doctor_profile.specialty}
                            </span>
                          )}
                        </span>
                        {currentId === doctor.id && (
                          <IconCheck className="ml-auto h-4 w-4 shrink-0" />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
